import * as S from './styles';
import SidebarTabs from '~/components/SidebarTabs';
import { useDataroomContext } from '../../providers/DataroomProvider';
import type { DataroomSubject } from '../../providers/DataroomProvider';
import { Label, Popup as _Popup } from 'semantic-ui-react';
import ProgressBar from '../ProgressBar';
import { DealStates } from '~/constants';
import styled from 'styled-components';
import { usePermissions } from '~/requests/permissions/usePermissions';
import Link from '~/components/Link';
import { useState, useEffect } from 'react';
import { Button, Modal } from '~/components';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import * as AdminApi from '~/api/admin';
import { useDataroomQuery } from '~/requests/dataroom';
import { usePortfolioContext, useProjectContext } from '~/hooks';
import { getDataroomCounts } from '~/utils/calculators/dataroomCounts';
import { Dataroom, DataroomCategory } from '~/types/dataroom';

const TabContainer = styled.div<{ disabled?: boolean }>`
  ${({ disabled }) => disabled && `
    position: relative;
    &::before {
      content: "";
      width: 100%;
      height: 100%;
      z-index: 1;
      background: var(--color-pure-white);
      opacity: 0.5;
      display: block;
      position: absolute;
    }
    &:hover {
      cursor: not-allowed;
    }
  `}
`;

const Popup = styled(_Popup)``;
Popup.defaultProps = {
  position: 'bottom center',
  inverted: true,
  on: ['hover', 'focus']
};

// Replicates the filter used by DataroomProvider so each subject can compute its own doclist.
function filterDoclistForSubject(
  categories: DataroomCategory[] | undefined,
  canUpdateDataroom: boolean,
  counts: ReturnType<typeof getDataroomCounts>,
): DataroomCategory[] {
  if (!categories) return [];
  return categories.filter(category => {
    if (category?.code === 'ADDITIONAL_DOCUMENTS' && !category.documentTypes.length) return false;
    if (canUpdateDataroom) return true;
    const { uploaded } = counts.categorizedCounts?.[category?.id as string] ?? { uploaded: 0 };
    return Boolean(uploaded);
  });
}

interface CategoryTabProps {
  category: DataroomCategory;
  counts: { uploaded: number; approved: number; total: number };
  isDiligence: boolean;
  active: boolean;
  onSelect: () => void;
}

function CategoryTab({ category, counts, isDiligence, active, onSelect }: CategoryTabProps) {
  const { uploaded, approved, total } = counts;
  return (
    <SidebarTabs.Tab active={active} onSelect={onSelect}>
      <S.TabContent data-testid={`category-${category?.id}-tab`}>
        <div>{category?.name}</div>
        {isDiligence && (
          <>
            {!!uploaded && !!total && uploaded === total && approved < total && (
              <Popup
                trigger={<Label circular tabIndex={0}>{uploaded}/{total}</Label>}
                content="All recommended documents have been uploaded but not all have been approved"
              />
            )}
            {!total && (
              <Popup
                trigger={<Label circular tabIndex={0}>N/A</Label>}
                content="No documents are required for this category"
              />
            )}
            {!!total && uploaded < total && (
              <Popup
                trigger={<Label circular tabIndex={0}>{uploaded}/{total}</Label>}
                content={`${uploaded}/${total} documents uploaded`}
              />
            )}
            {approved !== undefined && approved < total && (
              <Popup
                trigger={<Label circular color='green' tabIndex={0}>{approved}</Label>}
                content={`${approved} document${approved !== 1 ? 's' : ''} approved`}
              />
            )}
            {approved > 0 && total && approved === total && (
              <Popup
                trigger={<div className="complete-checkmark"><S.SuccessCheckmark tabIndex={0} /></div>}
                content="All recommended documents have been uploaded and approved"
              />
            )}
          </>
        )}
      </S.TabContent>
    </SidebarTabs.Tab>
  );
}

interface SubjectTreeNodeProps {
  subject: DataroomSubject;
  expanded: boolean;
  selectedSubjectId: string | undefined;
  selectedCategoryId: string | undefined;
  isDiligence: boolean;
  onToggleExpand: () => void;
  onSelectSubject: () => void;
  onSelectCategory: (subjectId: string, category: DataroomCategory) => void;
}

function SubjectTreeNode({
  subject,
  expanded,
  selectedSubjectId,
  selectedCategoryId,
  isDiligence,
  onToggleExpand,
  onSelectSubject,
  onSelectCategory,
}: SubjectTreeNodeProps) {
  const { permissions } = usePermissions();
  const counts = getDataroomCounts(subject.dataroom?.documents);
  const filteredDoclist = filterDoclistForSubject(
    subject.dataroom?.documents,
    Boolean(permissions?.canUpdateDataroom),
    counts,
  );
  const isSubjectSelected = subject.id === selectedSubjectId;
  const hasCategories = filteredDoclist.length > 0;

  return (
    <div>
      <S.SubjectHeader
        active={isSubjectSelected && !selectedCategoryId}
        onClick={() => {
          onSelectSubject();
          if (!expanded) onToggleExpand();
        }}
      >
        {hasCategories ? (
          <span
            role="button"
            tabIndex={0}
            onClick={e => { e.stopPropagation(); onToggleExpand(); }}
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <S.TwirlIcon expanded={expanded} />
          </span>
        ) : (
          <span style={{ width: '1rem' }} />
        )}
        <span>{subject.type === 'portfolio' ? 'Portfolio-level documents' : subject.label}</span>
      </S.SubjectHeader>
      {expanded && (
        <S.SubjectChildren>
          {filteredDoclist.map(category => {
            const c = counts.categorizedCounts?.[category?.id as string] ?? { uploaded: 0, approved: 0, total: 0 };
            return (
              <CategoryTab
                key={category.id}
                category={category}
                counts={c}
                isDiligence={isDiligence}
                active={isSubjectSelected && category.id === selectedCategoryId}
                onSelect={() => onSelectCategory(subject.id, category)}
              />
            );
          })}
          {!subject.dataroom && (
            <SidebarTabs.Tab>
              <S.TabContent><div style={{ fontStyle: 'italic', color: 'var(--color-charcoal)' }}>Loading…</div></S.TabContent>
            </SidebarTabs.Tab>
          )}
        </S.SubjectChildren>
      )}
    </div>
  );
}

export default function DocumentTypeSidebar() {
  const {
    dataroom,
    doclist,
    selectedCategory,
    setSelectedCategory,
    dataroomCounts,
    dealState,
    isSimplifiedUI,
    setEditingDocumentTypes,
    subjects,
    selectedSubject,
    setSelectedSubjectId,
  } = useDataroomContext();

  const { permissions } = usePermissions();
  const [uiToggleModalOpen, setUiToggleModalOpen] = useState(false);
  const isDiligence = dealState >= DealStates.IN_DILIGENCE;
  const { project } = useProjectContext();
  const { portfolio } = usePortfolioContext();
  const isPartOfPortfolio = portfolio || project?.portfolioId;

  const { updateDataroomCache } = useDataroomQuery();

  // Track which subject nodes are expanded. Default: the selected subject is expanded.
  const [expandedSubjectIds, setExpandedSubjectIds] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (selectedSubject?.id) {
      setExpandedSubjectIds(prev => prev[selectedSubject.id] ? prev : { ...prev, [selectedSubject.id]: true });
    }
  }, [selectedSubject?.id]);

  const { mutate: toggleDocumentsUI, status: toggleDocumentsUIStatus } = useMutation(async () => {
    try {
      const newDataroom = await AdminApi.toggleDataroomUI((dataroom as Dataroom).id);
      setUiToggleModalOpen(false);
      updateDataroomCache(newDataroom);
      setEditingDocumentTypes(false);
      toast.success('Successfully changed ' + (isPartOfPortfolio ? 'portfolio' : 'project ') + ' to ' + (!isSimplifiedUI ? 'simple' : 'power') + ' user interface');
    } catch (err) {
      console.error(err);
      toast.error('An error occurred while updating the UI. Please refresh the page and try again.');
    }
  });

  // Hide sidebar for simplified UI only when there are no subjects to switch between.
  if (isSimplifiedUI && !permissions.isAdmin && !subjects) {
    return null;
  }

  const handleSelectCategory = (subjectId: string, category: DataroomCategory) => {
    if (subjects && subjectId !== selectedSubject?.id) {
      setSelectedSubjectId(subjectId);
    }
    setSelectedCategory(category);
  };

  // Multi-subject (portfolio dataroom): render tree.
  if (subjects) {
    return (
      <div className="dataroom-sidebar">
        <SidebarTabs>
          {subjects.map(subject => (
            isSimplifiedUI ? (
              <SidebarTabs.Tab
                key={subject.id}
                active={subject.id === selectedSubject?.id}
                onSelect={() => setSelectedSubjectId(subject.id)}
              >
                <S.TabContent><div>{subject.type === 'portfolio' ? 'Portfolio-level documents' : subject.label}</div></S.TabContent>
              </SidebarTabs.Tab>
            ) : (
              <SubjectTreeNode
                key={subject.id}
                subject={subject}
                expanded={!!expandedSubjectIds[subject.id]}
                selectedSubjectId={selectedSubject?.id}
                selectedCategoryId={selectedCategory?.id}
                isDiligence={isDiligence}
                onToggleExpand={() => setExpandedSubjectIds(prev => ({ ...prev, [subject.id]: !prev[subject.id] }))}
                onSelectSubject={() => setSelectedSubjectId(subject.id)}
                onSelectCategory={handleSelectCategory}
              />
            )
          ))}
          {isDiligence && (
            <S.ProgressBarContainer>
              <ProgressBar />
            </S.ProgressBarContainer>
          )}
          {!isDiligence && permissions.isAdmin && (
            <Link style={{ marginTop: 'var(--x-large)', marginBottom: 'var(--large)' }} as="button" underline onClick={() => setUiToggleModalOpen(true)}>
              {isSimplifiedUI ? 'Switch to power user interface' : 'Switch to simple user interface'}
            </Link>
          )}
          {uiToggleModalOpen && (
            <Modal closeModal={() => toggleDocumentsUIStatus !== 'loading' && setUiToggleModalOpen(false)}>
              <Modal.Title>
                {isSimplifiedUI ? 'Switch to power user interface' : 'Switch to simple user interface'}
              </Modal.Title>
              <Modal.Content>
                This will change the interface to the {isSimplifiedUI ? 'power user' : 'simple user'} interface. Are you sure you want to continue?
              </Modal.Content>
              <Modal.Footer>
                <Button outlined primary onClick={() => setUiToggleModalOpen(false)} disabled={toggleDocumentsUIStatus === 'loading'}>
                  Cancel
                </Button>
                <Button primary disabled={toggleDocumentsUIStatus === 'loading'} loading={toggleDocumentsUIStatus === 'loading'} onClick={() => toggleDocumentsUI()}>
                  Confirm
                </Button>
              </Modal.Footer>
            </Modal>
          )}
        </SidebarTabs>
      </div>
    );
  }

  // Single-subject (project dataroom or anything else): existing flat behavior.
  return (
    <div className="dataroom-sidebar">
      <SidebarTabs>
        {isSimplifiedUI && (
          <SidebarTabs.Tab active>
            <S.TabContent>
              <div>Recommended Documents</div>
            </S.TabContent>
          </SidebarTabs.Tab>
        )}
        <TabContainer disabled={isSimplifiedUI}>
          {doclist?.map((category: any) => {
            const c = dataroomCounts.categorizedCounts?.[category?.id as string] ?? { uploaded: 0, approved: 0, total: 0 };
            return (
              <CategoryTab
                key={category.id}
                category={category}
                counts={c}
                isDiligence={isDiligence}
                active={!isSimplifiedUI && category?.id === selectedCategory?.id}
                onSelect={() => !isSimplifiedUI && setSelectedCategory(category)}
              />
            );
          })}
        </TabContainer>
        {isDiligence && (
          <S.ProgressBarContainer>
            <ProgressBar />
          </S.ProgressBarContainer>
        )}
        {!isDiligence && permissions.isAdmin && (
          <Link style={{ marginTop: 'var(--x-large)', marginBottom: 'var(--large)' }} as="button" underline onClick={() => setUiToggleModalOpen(true)}>
            {isSimplifiedUI ? 'Switch to power user interface' : 'Switch to simple user interface'}
          </Link>
        )}
        {uiToggleModalOpen && (
          <Modal closeModal={() => toggleDocumentsUIStatus !== 'loading' && setUiToggleModalOpen(false)}>
            <Modal.Title>
              {isSimplifiedUI ? 'Switch to power user interface' : 'Switch to simple user interface'}
            </Modal.Title>
            <Modal.Content>
              This will change the interface to the {isSimplifiedUI ? 'power user' : 'simple user'} interface. Are you sure you want to continue?
            </Modal.Content>
            <Modal.Footer>
              <Button outlined primary onClick={() => setUiToggleModalOpen(false)} disabled={toggleDocumentsUIStatus === 'loading'}>
                Cancel
              </Button>
              <Button primary disabled={toggleDocumentsUIStatus === 'loading'} loading={toggleDocumentsUIStatus === 'loading'} onClick={() => toggleDocumentsUI()}>
                Confirm
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </SidebarTabs>
    </div>
  );
}
